import { RpcError } from '../api/rpc-client';
import { type CountryCode } from '../config';

export type TaxIdFieldConfig = {
  helpTranslationKeyPrefix?: string;
  invalidMessageKey: string;
  labelKey: string;
  mask: string;
  placeholder: string;
  showHelpTip: boolean;
  tipAriaLabelKey?: string;
  tipHref?: string;
};

const TAX_ID_DIGITS_COUNT = 11;
const INVALID_TAX_ID_FORMAT_ERROR = 'invalid tax id format';
const TAX_ID_CONFLICT_ERROR = 'try err: conflict';
const CPF_PIX_KEY_NOT_REGISTERED_ERROR =
  'pix key is not registered in the pix-system';

export function getTaxIdDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, TAX_ID_DIGITS_COUNT);
}

function hasRepeatedDigits(value: string): boolean {
  return /^([0-9])\1+$/.test(value);
}

export function formatTaxIdInput(
  rawValue: string,
  countryCode: CountryCode,
): string {
  const digits = getTaxIdDigits(rawValue);

  if (countryCode === '+54') {
    const partA = digits.slice(0, 2);
    const partB = digits.slice(2, 10);
    const partC = digits.slice(10, 11);

    if (!partB) {
      return partA;
    }

    if (!partC) {
      return `${partA}-${partB}`;
    }

    return `${partA}-${partB}-${partC}`;
  }

  const partA = digits.slice(0, 3);
  const partB = digits.slice(3, 6);
  const partC = digits.slice(6, 9);
  const partD = digits.slice(9, 11);

  if (!partB) {
    return partA;
  }

  if (!partC) {
    return `${partA}.${partB}`;
  }

  if (!partD) {
    return `${partA}.${partB}.${partC}`;
  }

  return `${partA}.${partB}.${partC}-${partD}`;
}

export function hasValidTaxIdValue(value: string): boolean {
  const digits = getTaxIdDigits(value);

  return digits.length === TAX_ID_DIGITS_COUNT && !hasRepeatedDigits(digits);
}

function hasTaxIdErrorMessage(error: unknown, messagePart: string): boolean {
  return (
    error instanceof RpcError &&
    error.message.toLowerCase().includes(messagePart)
  );
}

export function isInvalidTaxIdError(error: unknown): boolean {
  return hasTaxIdErrorMessage(error, INVALID_TAX_ID_FORMAT_ERROR);
}

export function isTaxIdFieldError(error: unknown): boolean {
  return (
    isInvalidTaxIdError(error) ||
    hasTaxIdErrorMessage(error, TAX_ID_CONFLICT_ERROR) ||
    hasTaxIdErrorMessage(error, CPF_PIX_KEY_NOT_REGISTERED_ERROR)
  );
}

function resolveTaxIdConflictMessageKey(countryCode: CountryCode): string {
  return countryCode === '+54'
    ? 'registrationIdentityPersonalData.cuilInUse'
    : 'registrationIdentityPersonalData.cpfInUse';
}

export function resolveTaxIdErrorMessageKey(
  countryCode: CountryCode,
  error: unknown,
  invalidMessageKey: string,
  taxIdValue: string,
): string | null {
  if (hasTaxIdErrorMessage(error, TAX_ID_CONFLICT_ERROR)) {
    return resolveTaxIdConflictMessageKey(countryCode);
  }

  if (
    countryCode === '+55' &&
    hasTaxIdErrorMessage(error, CPF_PIX_KEY_NOT_REGISTERED_ERROR)
  ) {
    return 'registrationIdentityPersonalData.cpfPixNotRegistered';
  }

  const taxIdDigits = getTaxIdDigits(taxIdValue);

  if (
    isInvalidTaxIdError(error) ||
    (taxIdDigits.length === TAX_ID_DIGITS_COUNT &&
      !hasValidTaxIdValue(taxIdValue))
  ) {
    return invalidMessageKey;
  }

  return null;
}

export function resolveTaxIdFieldConfig(
  countryCode: CountryCode,
): TaxIdFieldConfig {
  if (countryCode === '+54') {
    return {
      helpTranslationKeyPrefix: 'registrationIdentityCuilHelp',
      invalidMessageKey: 'registrationIdentityPersonalData.cuilInvalid',
      labelKey: 'registrationIdentityPersonalData.cuilLabel',
      mask: '00-00000000-0',
      placeholder: '00-00000000-0',
      showHelpTip: true,
      tipAriaLabelKey: 'registrationIdentityPersonalData.cuilTipAria',
      tipHref: 'https://www.anses.gob.ar/consulta/constancia-de-cuil',
    };
  }

  return {
    helpTranslationKeyPrefix: 'registrationIdentityCpfHelp',
    invalidMessageKey: 'registrationIdentityPersonalData.cpfInvalid',
    labelKey: 'registrationIdentityPersonalData.cpfLabel',
    mask: '000.000.000-00',
    placeholder: '000.000.000-00',
    showHelpTip: true,
    tipAriaLabelKey: 'registrationIdentityPersonalData.cpfTipAria',
    tipHref: 'https://www.gov.br/pt-br',
  };
}
